import {
  Scissors,
  Leaf,
  Flower2,
  Droplet,
  Wrench,
  Sun,
  Check,
  Shovel,
  Phone,
  Truck,
  Mail,
} from "lucide-react";

const SERVICES = [
  {
    icon: Scissors,
    title: "Weekly & Bi-Weekly Lawn Maintenance",
    price: "Starting at $55 / visit",
    ideal:
      "Ideal for busy homeowners, rentals, HOAs, and anyone who wants a consistently clean yard.",
    items: [
      "Professional mowing (front & back)",
      "Precision edging along walkways, driveways, and beds",
      "Trimming around fences, AC units, and obstacles",
      "Blowing of all hard surfaces",
      "Seasonal mowing height adjustments",
      "Light weed control in turf",
    ],
  },
  {
    icon: Leaf,
    title: "Hedging & Shrub Shaping",
    price: "$45 – $95 depending on shrub count",
    items: [
      "Clean shaping for a manicured look",
      "Removal of overgrowth",
      "Line correction for symmetry",
      "Seasonal health trimming",
    ],
  },
  {
    icon: Flower2,
    title: "Flower Bed Cleaning & Refresh",
    price: "$65 – $185 depending on bed size",
    items: [
      "Weed removal",
      "Soil turning & bed reshaping",
      "Mulch top-off (mulch available at cost + install)",
      "Planting seasonal color (optional)",
    ],
  },
  {
    icon: Droplet,
    title: "Power Washing for Curb Appeal",
    price:
      "Driveways $85–$145 · Sidewalks/Porch $45–$75 · Full Front Wash $165–$225",
    items: [
      "Driveways",
      "Sidewalks",
      "Front porch",
      "Entryways",
      "HOA-ready curb appeal boost",
    ],
  },
  {
    icon: Wrench,
    title: "Full Property Cleanups",
    price: "$225 – $475 depending on lot size",
    ideal: "Perfect for rentals, Airbnbs, or neglected yards.",
    items: [
      "Overgrowth removal",
      "Deep hedging",
      "Bed overhaul",
      "Haul-off included",
    ],
  },
  {
    icon: Shovel,
    title: "Landscaping & Hardscape Design",
    price: "Call for Pricing & Custom Quote",
    callForQuote: true,
    ideal:
      "Custom landscape installs, hardscape, and full curb-appeal makeovers — built one yard at a time.",
    items: [
      "Paver walkways, patios & driveways",
      "Decorative stone, gravel & xeriscape installs",
      "Retaining walls & garden borders",
      "Sod installation & turf replacement",
      "Custom planting, beds & tree installs",
      "On-site consultation included",
    ],
  },
  {
    icon: Truck,
    title: "Heavy Debris & Junk Removal",
    price: "Call or Email for a Quote",
    callForQuote: true,
    ideal:
      "Home remodel site prep, post-construction haul-offs, tree & brush clearing, and full-property junk removal — heavy work done clean.",
    items: [
      "Home remodel site prep & jobsite cleanup",
      "Tree, limb & brush removal",
      "Construction debris & concrete haul-off",
      "Old furniture, appliances & garage clean-outs",
      "Storm damage & overgrown lot clearing",
      "Same-week scheduling available for remodel crews",
    ],
  },
];

const PACKAGES = [
  {
    name: "Essential Cut & Clean",
    price: "$35 – $75 / visit",
    desc: "Weekly or bi-weekly mowing, edging, blowing, and light weed control.",
  },
  {
    name: "Curb Appeal Upgrade",
    price: "$145 – $195",
    desc: "Hedging, flower beds, mulch, and spot power-washing.",
  },
  {
    name: "Ironclad Full Property Makeover",
    price: "$325 – $475",
    desc: "Deep cleanup, power-washing, bed overhaul, haul-off, and full reset.",
  },
];

export default function ServicesDetail() {
  return (
    <section
      id="services-detail"
      data-testid="services-detail-section"
      className="py-24 md:py-32 bg-background border-t border-border"
    >
      <div className="container-ic">
        <div className="max-w-3xl mb-16 md:mb-20">
          <div className="overline mb-4">Full Service Menu</div>
          <h2 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter leading-[0.95]">
            Premium lawn & <br />
            <span className="text-primary">curb appeal</span> services
          </h2>
          <p className="mt-6 text-muted-foreground text-lg max-w-2xl">
            Built for Central Texas homes. Ironclad Yard Services delivers disciplined,
            reliable, high-quality lawn care with a focus on curb appeal, consistency,
            and clean lines — engineered for Austin-area lawns, heat, and HOA
            expectations.
          </p>
        </div>

        {/* Service cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-border border border-border mb-20">
          {SERVICES.map((s, idx) => {
            const Icon = s.icon;
            return (
              <article
                key={s.title}
                className="bg-background p-8 md:p-10 flex flex-col"
                data-testid={`service-detail-${idx}`}
              >
                <div className="flex items-start justify-between mb-5">
                  <div className="w-12 h-12 border border-primary/30 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <span className="text-xs tracking-widest text-muted-foreground">
                    0{idx + 1}
                  </span>
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight leading-tight">
                  {s.title}
                </h3>
                <div className="mt-3 text-primary font-semibold text-sm tracking-wide">
                  {s.price}
                </div>
                {s.callForQuote && (
                  <div className="mt-4 flex flex-wrap gap-2 self-start">
                    <a
                      href="tel:5129480676"
                      className="inline-flex items-center gap-2 border border-primary/60 bg-primary/10 hover:bg-primary hover:text-primary-foreground text-primary text-xs uppercase tracking-[0.2em] font-bold px-4 py-2 transition-colors"
                      data-testid={`service-detail-call-${idx}`}
                    >
                      <Phone className="h-3.5 w-3.5" />
                      Call 512-948-0676
                    </a>
                    <a
                      href={`mailto:ironcladatx@gmail.com?subject=Quote%20Request%20%E2%80%94%20${encodeURIComponent(s.title)}&body=Hi%20Ironclad%2C%20I%27d%20like%20a%20quote%20for%20${encodeURIComponent(s.title)}.%0A%0AProperty%20address%3A%20%5B%5D%0AScope%2FDetails%3A%20%5B%5D%0AIdeal%20timeline%3A%20%5B%5D%0AName%3A%20%5B%5D%0APhone%3A%20%5B%5D`}
                      className="inline-flex items-center gap-2 border border-border bg-black/40 hover:border-primary hover:text-primary text-foreground text-xs uppercase tracking-[0.2em] font-bold px-4 py-2 transition-colors"
                      data-testid={`service-detail-email-${idx}`}
                    >
                      <Mail className="h-3.5 w-3.5" />
                      Email for Quote
                    </a>
                  </div>
                )}

                <ul className="mt-6 space-y-2.5">
                  {s.items.map((it) => (
                    <li
                      key={it}
                      className="flex items-start gap-3 text-sm text-foreground/90"
                    >
                      <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>

                {s.ideal && (
                  <p className="mt-6 text-xs uppercase tracking-widest text-muted-foreground border-t border-border pt-4">
                    {s.ideal}
                  </p>
                )}
              </article>
            );
          })}
        </div>

        {/* Seasonal Packages */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <Sun className="h-5 w-5 text-primary" />
            <div className="overline">Seasonal Packages · Spring & Summer</div>
          </div>
          <h3 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tighter">
            Bundled. <span className="text-primary">Simplified.</span>
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
          {PACKAGES.map((p, idx) => (
            <div
              key={p.name}
              className="bg-background p-8 md:p-10"
              data-testid={`package-${idx}`}
            >
              <div className="overline text-muted-foreground mb-3">
                Package 0{idx + 1}
              </div>
              <h4 className="font-display text-2xl font-bold uppercase tracking-tight leading-tight">
                {p.name}
              </h4>
              <div className="mt-4 font-display text-3xl font-black text-primary">
                {p.price}
              </div>
              <p className="mt-5 text-sm text-muted-foreground leading-relaxed">
                {p.desc}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap items-center gap-4">
          <a href="#booking" className="btn-primary" data-testid="services-detail-book">
            Request Estimate
          </a>
          <a href="tel:5129480676" className="btn-outline" data-testid="services-detail-call">
            Call 512-948-0676
          </a>
        </div>
      </div>
    </section>
  );
}
